import {
  collection,
  doc,
  getDocs,
  getDoc,
  orderBy,
  query,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../../backend/firebaseConfig";
import {
  PLAN_TYPE_AUDIT_PATH,
  PLAN_TYPE_MAPPING_PATH,
} from "./planTypeMappingConstants";

const mappingCollection = () => collection(db, ...PLAN_TYPE_MAPPING_PATH);
const auditCollection = () => collection(db, ...PLAN_TYPE_AUDIT_PATH);
const cleanActor = (actor = "") =>
  String(actor || localStorage.getItem("userEmail") || "Unknown user")
    .trim()
    .toLowerCase();

export const getPlanTypeMappings = async () => {
  const snapshot = await getDocs(
    query(mappingCollection(), orderBy("planTypeNameLower", "asc")),
  );

  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

export const getPlanTypeMapping = async (planTypeId) => {
  const snapshot = await getDoc(doc(mappingCollection(), planTypeId));
  if (!snapshot.exists()) throw new Error("IO plan type mapping not found.");
  return { id: snapshot.id, ...snapshot.data() };
};

export const getPlanTypeAuditLogs = async () => {
  const snapshot = await getDocs(
    query(auditCollection(), orderBy("createdAt", "desc")),
  );

  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

export const savePlanTypeMappings = async ({ rows, actor, sourceFileName }) => {
  const validRows = rows.filter((row) => row.isValid !== false);
  if (!validRows.length) throw new Error("There are no valid rows to save.");

  const now = new Date().toISOString();
  const performedBy = cleanActor(actor);
  const batch = writeBatch(db);

  validRows.forEach((row) => {
    batch.set(
      doc(mappingCollection(), row.planTypeId),
      {
        planTypeId: row.planTypeId,
        planTypeName: row.planTypeName,
        planTypeNameLower: row.planTypeName.toLowerCase(),
        planCategory: row.planCategory,
        portfolioCategory: row.portfolioCategory,
        productGroup: row.productGroup,
        sourceFileName: sourceFileName || "",
        updatedAt: now,
        updatedBy: performedBy,
      },
      { merge: true },
    );
  });

  batch.set(doc(auditCollection()), {
    action: "imported",
    summary: `Imported ${validRows.length} IO plan type mappings`,
    recordCount: validRows.length,
    sourceFileName: sourceFileName || "",
    performedBy,
    createdAt: now,
  });

  await batch.commit();
  return { savedCount: validRows.length };
};

export const updatePlanTypeIoFields = async ({ differences, actor }) => {
  if (!differences.length) return { updatedCount: 0 };

  const now = new Date().toISOString();
  const performedBy = cleanActor(actor);
  const batch = writeBatch(db);

  differences.forEach(({ planTypeId, expected }) => {
    batch.update(doc(mappingCollection(), planTypeId), {
      portfolioCategory: expected.portfolioCategory,
      productGroup: expected.productGroup,
      updatedAt: now,
      updatedBy: performedBy,
    });
  });

  batch.set(doc(auditCollection()), {
    action: "io_fields_corrected",
    summary: `Corrected IO Portfolio Category and IO Product Group for ${differences.length} plan type mappings`,
    recordCount: differences.length,
    performedBy,
    createdAt: now,
  });

  await batch.commit();
  return { updatedCount: differences.length };
};

export const updatePlanTypeCategory = async ({
  mapping,
  planCategory,
  actor,
}) => {
  if (!mapping?.id || !planCategory) {
    throw new Error("A mapping and plan category are required.");
  }

  const now = new Date().toISOString();
  const performedBy = cleanActor(actor);
  const batch = writeBatch(db);

  batch.update(doc(mappingCollection(), mapping.id), {
    planCategory,
    updatedAt: now,
    updatedBy: performedBy,
  });

  batch.set(doc(auditCollection()), {
    action: "category_updated",
    summary: `Updated ${mapping.planTypeName}`,
    planTypeId: mapping.planTypeId,
    planTypeName: mapping.planTypeName,
    oldCategory: mapping.planCategory || "",
    newCategory: planCategory,
    performedBy,
    createdAt: now,
  });

  await batch.commit();
};
